import { useTranslation } from "react-i18next";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "./index";
import { Controller, useForm } from "react-hook-form";
import { Grid, Stack } from "@mantine/core";
import MultiSelect, { transformDataToSelectOption } from "@/components/atoms/MultiSelect";
import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import { zodResolver } from "@hookform/resolvers/zod";
import { AddPartnerFormInput, AddPartnerValidationSchema } from "./validation/AddPartnerValidationSchema";
import TextInput from "@/components/atoms/TextInput";
import CTA from "@/components/atoms/CTA";
import { useMutation } from "@tanstack/react-query";
import { getCompanyByOrgNumber, inviteCompany } from "@/services/inviteCompany";
import { transformCompanyData } from "@/utils/duns";
import { notifications } from "@mantine/notifications";
import React from "react";
import { useGetContractTemplates } from "@/services/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import Checkbox from "@/components/atoms/Checkbox";

export const Customers: React.FC = () => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  return (
    <Stack data-testid="customers-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.partners")}
      </Heading>
      <AddPartnerForm companyId={currentCompany?.companyId} />
    </Stack>
  );
};

export default Customers;

const AddPartnerForm = ({ companyId }: { companyId: number | undefined }) => {
  const { t } = useTranslation();
  const { data } = useGetContractTemplates(companyId as number, {
    enabled: !!companyId,
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
    getValues,
    setValue,
  } = useForm<AddPartnerFormInput>({
    resolver: zodResolver(AddPartnerValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    values: {
      orgNumber: "",
      companyName: "",
      country: "NO",
      address: "",
      postalCode: "",
      region: "",
      city: "",
      duns: "",
      contractTemplateIds: [],
      outsideEu: false,
    },
  });

  const { mutateAsync: handleSearch, isPending } = useMutation({
    mutationFn: async (orgNumber: string) => {
      return await getCompanyByOrgNumber(orgNumber);
    },
    onSuccess: (res) => {
      const transformedData = res ? transformCompanyData(res.data) : null;
      if (transformedData) {
        setValue("companyName", transformedData.name || "");
        setValue("country", transformedData.countryCode || "");
        setValue("address", transformedData.address || "");
        setValue("postalCode", transformedData.postalCode || "");
        setValue("region", transformedData.region || "");
        setValue("city", transformedData.city || "");
        setValue("duns", transformedData.duns || "");
      } else {
        notifications.show(
          ErrorNotificationObj(
            t("notifications:generic.error"),
            t("notifications:settings.companySearchError"),
            "company-search-no-results"
          )
        );
      }
    },
    onError: () => {
      notifications.show({
        title: t("notifications:generic.error"),
        message: t("notifications:companySearchError"),
      });
    },
  });

  const { mutateAsync: inviteCompanyMutation, isPending: isPendingInviteCompany } = useMutation({
    mutationFn: async (data: AddPartnerFormInput) => {
      const contractTemplateIds = data.contractTemplateIds.map((templateId) => parseInt(templateId));
      return await inviteCompany(data.duns, contractTemplateIds);
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:settings.companyInvited"),
          "partner-invited-success"
        )
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:settings.companyInvitedError"),
          "partner-invited-error"
        )
      );
    },
  });

  const onSubmit = async (data: AddPartnerFormInput) => {
    await inviteCompanyMutation(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:partnersSettings.addCompany")}
            </Text>
          </SettingsHeader>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="orgNumber"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="orgNumber"
                  data-testid="org-number-input"
                  label={t("settings:partnersSettings.organisationNumber")}
                  placeholder=""
                  value={field.value?.replace(/\s/g, "") || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
            <Controller
              control={control}
              name="outsideEu"
              render={({ field }) => (
                <Checkbox
                  mt={10}
                  label={t("settings:partnersSettings.outsideEu")}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.currentTarget.checked)}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTA
              mt={25}
              intent="secondary"
              onClick={() => handleSearch(getValues("orgNumber"))}
              disabled={isPending || !isValid}
            >
              {t("settings:partnersSettings.getInfo")}
            </CTA>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="companyName"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  disabled={true}
                  id="companyName"
                  label={t("settings:partnersSettings.companyName")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="country"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="country"
                  disabled={true}
                  label={t("settings:partnersSettings.country")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="address"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="address"
                  disabled={true}
                  label={t("settings:partnersSettings.address")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="city"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="city"
                  disabled={true}
                  label={t("settings:partnersSettings.city")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="postalCode"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="postalCode"
                  disabled={true}
                  label={t("settings:partnersSettings.postalCode")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="region"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="region"
                  disabled={true}
                  label={t("settings:partnersSettings.region")}
                  value={field.value || ""}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="contractTemplateIds"
              render={({ field, fieldState }) => (
                <MultiSelect
                  label={t("settings:templates.selectTemplates")}
                  data={transformDataToSelectOption(data || [], "templateId", "name", t)}
                  error={fieldState.error?.message || undefined}
                  {...field}
                />
              )}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA
          type={"submit"}
          disabled={!isValid}
          id="save"
          data-testid="submit-btn"
          size="lg"
          loading={isPending || isPendingInviteCompany}
        >
          {t("actions:add")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};
